import { createAction, createSlice } from '@reduxjs/toolkit';
import type {
  StaffAuthResultDto,
  StaffChallengeIssuedDto,
  StaffLoginInput,
  StaffMeDto,
  StaffSetupInput,
  StaffVerifyInput,
} from '@icebreaker/shared/staff';
import { type ApiErrorBody } from '@icebreaker/shared/errors';
import { createAsyncActions, initialAsyncState, type AsyncState } from '@icebreaker/shared/store';

/**
 * PERSONEL OTURUMU (panel kimliği ayrımı · Karar 3b).
 *
 * Ön ekler `staffAuth/...` — ürünün `auth/...` ön ekinden AYRIDIR ve bu
 * bilinçlidir: iki uygulama aynı depoyu paylaşmasa da `invalidation-coverage`
 * kapısı ön ekleri DEPO GENELİNDE enumere eder; aynı adı taşımak iki farklı
 * kimliği tek bir önbellek anahtarı ailesi gibi gösterirdi.
 *
 * `requestOtp` ve `panelLogin` KALDIRILDI: ürünün parolasız OTP yolu panele
 * hiç açılmaz (Karar 4c — ilk kurulum DAVETLE yapılır, self-servis kod ile
 * değil).
 */
export const staffLogin = createAsyncActions<StaffLoginInput, StaffChallengeIssuedDto>(
  'staffAuth/login',
);
export const staffVerify = createAsyncActions<StaffVerifyInput, StaffAuthResultDto>(
  'staffAuth/verify',
);
/** Davet jetonu + şifre → oturum. Panelde başka bir kurtarma yolu YOKTUR. */
export const staffSetup = createAsyncActions<StaffSetupInput, StaffAuthResultDto>(
  'staffAuth/setup',
);
export const restoreSession = createAsyncActions<
  void,
  { me: StaffMeDto; accessToken: string } | null
>('staffAuth/restoreSession');
export const signOut = createAsyncActions<void, void>('staffAuth/signOut');
/**
 * PERSONEL OTURUMU SUNUCUDA BİTTİ (panel küçük işler 1 · B-02). Korumalı bir
 * istek 401 aldı, yenileme de reddedildi (7 gün doldu, oturum iptal edildi,
 * hesap askıya alındı). Oturum sınırıdır: bütün dilimler sıfırlanır, kapı
 * giriş ekranına götürür ve geri dönüş adresini taşır. Ekranda "süresi doldu"
 * hata satırıyla kalınmaz.
 */
export const sessionExpired = createAction('staffAuth/sessionExpired');

export interface AuthState {
  staff: StaffMeDto | null;
  isAuthenticated: boolean;
  /** İlk oturum diriltme denemesi bitti mi (kapı kararları bunu bekler). */
  bootstrapped: boolean;
  /**
   * OTURUM HAKKINDA KARAR VERİLEMEDİ (429/5xx/ağ). Bu bayrak açıkken kapı
   * YÖNLENDİRMEZ ve KİLİTLEMEZ; geçici hata + "Tekrar dene" gösterir.
   * İçerik yine ÇİZİLMEZ — fail-closed.
   */
  sessionUndecided: boolean;
  /**
   * Oturum sunucuda bitti ve kullanıcı giriş ekranına bu yüzden geldi. Giriş
   * ekranı nedenini bir kez yazar; yeni bir giriş denemesi bayrağı düşürür.
   */
  sessionExpiredNotice: boolean;
  challenge: {
    email: string | null;
    resendAvailableInSeconds: number;
    codeSent: boolean;
  };
  requestState: AsyncState<ApiErrorBody>;
  verifyState: AsyncState<ApiErrorBody>;
  setupState: AsyncState<ApiErrorBody>;
  signOutState: AsyncState<ApiErrorBody>;
}

const initialState: AuthState = {
  staff: null,
  isAuthenticated: false,
  bootstrapped: false,
  sessionUndecided: false,
  sessionExpiredNotice: false,
  challenge: { email: null, resendAvailableInSeconds: 60, codeSent: false },
  requestState: { ...initialAsyncState },
  verifyState: { ...initialAsyncState },
  setupState: { ...initialAsyncState },
  signOutState: { ...initialAsyncState },
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    challengeReset(state) {
      state.challenge = { email: null, resendAvailableInSeconds: 60, codeSent: false };
      state.requestState = { ...initialAsyncState };
      state.verifyState = { ...initialAsyncState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(staffLogin.request, (state, action) => {
        state.sessionExpiredNotice = false;
        state.requestState.status = 'loading';
        state.requestState.error = null;
        state.challenge.email = action.payload.email;
      })
      .addCase(staffLogin.success, (state, action) => {
        state.requestState.status = 'succeeded';
        state.challenge.codeSent = true;
        state.challenge.resendAvailableInSeconds = action.payload.resendAvailableInSeconds;
        // Sayaç yeniden göndermede de başa dönsün diye damga şart.
        state.requestState.lastLoadedAt = Date.now();
      })
      .addCase(staffLogin.failure, (state, action) => {
        state.requestState.status = 'failed';
        state.requestState.error = action.payload;
      })
      .addCase(staffVerify.request, (state) => {
        state.verifyState.status = 'loading';
        state.verifyState.error = null;
      })
      .addCase(staffVerify.success, (state, action) => {
        state.verifyState.status = 'succeeded';
        state.staff = {
          account: action.payload.account,
          permissions: [...action.payload.permissions],
        };
        state.isAuthenticated = true;
        state.bootstrapped = true;
        state.sessionUndecided = false;
      })
      .addCase(staffVerify.failure, (state, action) => {
        state.verifyState.status = 'failed';
        state.verifyState.error = action.payload;
      })
      /*
       * İLK KURULUM DA OTURUM AÇAR: davet jetonu tüketildiğinde personel
       * yeniden giriş yapmak zorunda BIRAKILMAZ (aksi hâlde şifresini yeni
       * belirlemiş kişi kod adımına geri düşerdi).
       */
      .addCase(staffSetup.request, (state) => {
        state.setupState.status = 'loading';
        state.setupState.error = null;
      })
      .addCase(staffSetup.success, (state, action) => {
        state.setupState.status = 'succeeded';
        state.staff = {
          account: action.payload.account,
          permissions: [...action.payload.permissions],
        };
        state.isAuthenticated = true;
        state.bootstrapped = true;
        state.sessionUndecided = false;
      })
      .addCase(staffSetup.failure, (state, action) => {
        state.setupState.status = 'failed';
        state.setupState.error = action.payload;
      })
      .addCase(restoreSession.success, (state, action) => {
        state.bootstrapped = true;
        state.sessionUndecided = false;
        if (action.payload) {
          state.staff = {
            account: action.payload.me.account,
            permissions: [...action.payload.me.permissions],
          };
          state.isAuthenticated = true;
        }
      })
      /*
       * Diriltme HATASI "oturum yok" DEMEK DEĞİLDİR: 401/403 `success(null)`
       * yolundan geçer, buraya yalnız 429/5xx/ağ hatası düşer.
       */
      .addCase(restoreSession.failure, (state) => {
        state.bootstrapped = true;
        state.sessionUndecided = true;
      })
      .addCase(signOut.request, (state) => {
        state.signOutState.status = 'loading';
        state.signOutState.error = null;
      })
      .addCase(signOut.success, () => ({ ...initialState, bootstrapped: true }))
      /*
       * Kök sıfırlama bu aksiyonda dilimi zaten başlangıca döndürür
       * (`SESSION_BOUNDARY_ACTIONS`); burada yalnız "karar verildi: oturum yok"
       * yazılır. `bootstrapped` düşük kalsaydı kapı sonsuz iskelette kalırdı.
       */
      .addCase(sessionExpired, () => ({
        ...initialState,
        bootstrapped: true,
        sessionExpiredNotice: true,
      }))
      /*
       * ÇIKIŞ HATASI OTURUM SINIRI DEĞİLDİR: HttpOnly çerezi istemci silemez.
       * İstek sunucuya ulaşmadıysa oturum CANLIDIR; "çıkıldı" göstermek yalan
       * olur ve paylaşılan cihazda panel açık kalır.
       */
      .addCase(signOut.failure, (state, action) => {
        state.signOutState.status = 'failed';
        state.signOutState.error = action.payload;
      });
  },
});

export const { challengeReset } = slice.actions;
export const authReducer = slice.reducer;
